import AssessmentIcon from '@mui/icons-material/Assessment';
import BadgeIcon from '@mui/icons-material/Badge';
import BusinessIcon from '@mui/icons-material/Business';
import CategoryIcon from '@mui/icons-material/Category';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import EngineeringIcon from '@mui/icons-material/Engineering';
import FolderIcon from '@mui/icons-material/Folder';
import HistoryIcon from '@mui/icons-material/History';
import HomeIcon from '@mui/icons-material/Home';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box } from '@mui/material';
import { createAppScope, permission } from '@nohara-dxdev/oidc-core';
import { LoadingScreen } from '@nohara-dxdev/react-core';
import {
  useUserOrgs,
  BreadcrumbItem,
  DashboardLayout,
  DashboardNavMenuItem,
  useOidc,
  useOrg,
  useScopeIds,
} from '@nohara-dxdev/react-oidc';
import { PropsWithChildren, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import NotFoundRoute from '@/app/routes/not-found';
import { env } from '@/config/env';
import { paths } from '@/config/paths';
import { AiChatDrawer, AiChatFab, AiRuntimeProvider, ChatDrawerProvider } from '@/features/ai-chat';
import { AppAssignmentCheck } from '@/features/apps/components/app-assignment-check';
import { useFeatureFlag } from '@/features/feature-flags';
import { NotificationBannerPanel, NotificationBell } from '@/features/notifications';
import { useProject } from '@/features/projects/api/get-project';
import { GlobalCreateTaskButton } from '@/features/tasks';

const readRolePermissions = [permission.role.read];
const readUserPermissions = [permission.user.read];
const updateOrgPermissions = [permission.org.update];
const readProjectPermissions = [permission.project.read];
const readItemTemplatePermissions = [permission.itemTemplate.read];
const readBuildingPermissions = [permission.building.read];
const readSettingsPermissions = [permission.settings.read];
const readSystemPermissions = [permission.system.read];
const readReportPermissions = [permission.report.read];
const readActivityPermissions = [permission.activity.read];
const createTaskPermissions = [permission.task.create];
const readPowerTypePermissions = [permission.powerType.read];

export const AppLayout = ({ children }: PropsWithChildren) => {
  const { userId } = useOidc();
  const { t } = useTranslation();
  const context = useScopeIds();
  const { orgId, projectId } = context;
  const { data: orgs } = useUserOrgs(userId);
  const { enabled: aiAgentEnabled, isLoading: aiAgentLoading } = useFeatureFlag('ai-agent');
  // const { entities } = useScope();
  // const org = orgId ? (entities.get(orgId) as OrgEntity) : undefined;

  const { data: org } = useOrg(orgId);
  const { data: project } = useProject(projectId);

  const hasMoreThanOneOrg = !!orgs?.length && orgs.length > 1;

  const headerCrumbs = useMemo((): BreadcrumbItem[] | undefined => {
    if (projectId && orgId) {
      return [
        {
          label: project?.name ?? t('common.loading'),
          href: paths.org.project.dashboard.getHref(orgId, projectId),
        },
      ];
    }
    if (orgId) {
      return [
        {
          label: org?.name ?? t('common.loading'),
          href: paths.org.dashboard.getHref(orgId),
        },
      ];
    }
    return undefined;
  }, [projectId, project, orgId, org, t]);

  const pageTitle = useMemo(() => {
    if (projectId) {
      return project?.name ?? t('common.loading');
    }
    if (orgId) {
      return org?.name ?? t('common.loading');
    }
    return t('navigation.appTitle');
  }, [projectId, project, orgId, org, t]);
  const scope = orgId
    ? projectId
      ? createAppScope(env.APP_ID, orgId, 'project', projectId)
      : createAppScope(env.APP_ID, orgId)
    : '';

  const sections = useMemo((): DashboardNavMenuItem[][] => {
    const sections: DashboardNavMenuItem[][] = [];
    const firstSection: DashboardNavMenuItem[] = [];
    const secondSection: DashboardNavMenuItem[] = [];
    const thirdSection: DashboardNavMenuItem[] = [];

    if (orgId) {
      if (projectId) {
        firstSection.push({
          path: paths.org.dashboard.getHref(orgId),
          icon: <FolderIcon />,
          label: t('navigation.menu.backToApp'),
          context,
        });
      }

      secondSection.push({
        path: projectId
          ? paths.org.project.dashboard.getHref(orgId, projectId)
          : paths.org.dashboard.getHref(orgId),
        icon: <DashboardIcon />,
        label: t('navigation.menu.dashboard'),
        context,
      });

      if (!projectId) {
        secondSection.push({
          path: paths.org.projectsList.getHref(orgId),
          icon: <FolderIcon />,
          label: t('navigation.menu.projects'),
          requireAll: readProjectPermissions,
          scope,
          context,
        });

        secondSection.push({
          path: paths.org.itemTemplates.getHref(orgId),
          icon: <ContentCopyIcon />,
          label: t('navigation.menu.itemTemplates'),
          requireAll: readItemTemplatePermissions,
          scope,
          context,
        });
      }

      if (projectId) {
        // Menu items above the separator: Room Management, Item Management, Building Information
        secondSection.push({
          path: paths.org.project.room.getHref(orgId, projectId),
          icon: <MeetingRoomIcon />,
          label: t('navigation.menu.room'),
          requireAll: readBuildingPermissions,
          scope: scope,
          context,
        });

        secondSection.push({
          path: paths.org.project.system.getHref(orgId, projectId),
          icon: <CategoryIcon />,
          label: t('navigation.menu.system'),
          requireAll: readSystemPermissions,
          scope: scope,
          context,
        });

        secondSection.push({
          path: paths.org.project.buildings.getHref(orgId, projectId),
          icon: <BusinessIcon />,
          label: t('navigation.menu.buildings'),
          requireAll: readBuildingPermissions,
          scope: scope,
          context,
        });

        // Menu items below the separator: Reports, System Categories, Power Types, Settings, Users, Activity Log
        thirdSection.push({
          path: paths.org.project.reports.getHref(orgId, projectId),
          icon: <AssessmentIcon />,
          label: t('navigation.menu.reports'),
          requireAll: readReportPermissions,
          scope: scope,
          context,
        });

        thirdSection.push({
          path: paths.org.project.systems.getHref(orgId, projectId),
          icon: <EngineeringIcon />,
          label: t('navigation.menu.systemManagement'),
          requireAll: readSystemPermissions,
          scope: scope,
          context,
        });

        thirdSection.push({
          path: paths.org.project.powerTypes.getHref(orgId, projectId),
          icon: <ElectricBoltIcon />,
          label: t('navigation.menu.powerTypes'),
          requireAll: readPowerTypePermissions,
          scope: scope,
          context,
        });

        thirdSection.push({
          path: paths.org.project.settings.getHref(orgId, projectId),
          icon: <SettingsIcon />,
          label: t('navigation.menu.settings'),
          requireAll: readSettingsPermissions,
          scope: scope,
          context,
        });

        thirdSection.push({
          path: paths.org.project.users.getHref(orgId, projectId),
          icon: <PeopleIcon />,
          label: t('navigation.menu.users'),
          requireAll: readUserPermissions,
          scope: scope,
          context,
        });

        thirdSection.push({
          path: paths.org.project.activity.getHref(orgId, projectId),
          icon: <HistoryIcon />,
          label: t('navigation.menu.activityLog'),
          requireAll: readActivityPermissions,
          scope: scope,
          context,
        });
      }

      if (!projectId) {
        secondSection.push({
          path: paths.org.roles.getHref(orgId),
          icon: <BadgeIcon />,
          label: t('navigation.menu.roles'),
          requireAll: readRolePermissions,
          scope: scope,
          context,
        });
      }

      // For organization context, add Users, Settings, and Activity Log to secondSection
      if (!projectId) {
        secondSection.push(
          {
            path: paths.org.users.getHref(orgId),
            icon: <PeopleIcon />,
            label: t('navigation.menu.users'),
            requireAll: readUserPermissions,
            scope: scope,
            context,
          },
          {
            path: paths.org.settings.getHref(orgId),
            icon: <SettingsIcon />,
            label: t('navigation.menu.settings'),
            requireAll: readSettingsPermissions,
            scope: scope,
            context,
          },
          {
            path: paths.org.activity.getHref(orgId),
            icon: <HistoryIcon />,
            label: t('navigation.menu.activityLog'),
            requireAll: readActivityPermissions,
            scope: scope,
            context,
          },
        );
      }

      if (!projectId) {
        secondSection.push({
          path: paths.org.orgProfile.getHref(orgId),
          icon: <SettingsIcon />,
          label: t('navigation.menu.orgProfile'),
          requireAll: updateOrgPermissions,
          scope: scope,
          context,
        });
      }
    }

    if (firstSection.length > 0) {
      sections.push(firstSection);
    }

    if (secondSection.length > 0) {
      sections.push(secondSection);
    }

    if (thirdSection.length > 0) {
      sections.push(thirdSection);
    }

    return sections;
  }, [orgId, t, context, projectId, scope]);

  const bottomSections = useMemo((): DashboardNavMenuItem[][] | undefined => {
    const bottom: DashboardNavMenuItem[][] = [];

    if (orgId && projectId) {
      bottom.push([
        {
          overrideElement: (
            <Box px={1} py={1} display="flex" justifyContent="center" width="100%">
              <GlobalCreateTaskButton showName />
            </Box>
          ),
          requireAll: createTaskPermissions,
          scope,
          context,
        },
      ]);
    }

    if (hasMoreThanOneOrg) {
      bottom.push([
        {
          path: paths.home.path,
          icon: <HomeIcon />,
          label: t('navigation.menu.home'),
          context,
        },
      ]);
    }

    return bottom.length > 0 ? bottom : undefined;
  }, [orgId, projectId, hasMoreThanOneOrg, scope, context, t]);

  const content = (
    <DashboardLayout
      sections={sections}
      bottomSections={bottomSections}
      navTitle={org?.name ?? t('navigation.title')}
      pageTitle={pageTitle}
      breadcrumbs={headerCrumbs}
      paths={{
        logout: paths.auth.login.path,
        userProfile: paths.org.userProfile.getHref(),
      }}
      appVersion={`${t('common.appVersion')}: ${env.FRONTEND_VERSION}`}
      backendVersion={`${t('common.backendVersion')}: ${env.BACKEND_VERSION}`}
      toolbarElements={[<NotificationBell key="notification-bell" />]}
    >
      <NotificationBannerPanel />
      {children}
    </DashboardLayout>
  );

  return (
    <AppAssignmentCheck loadingElement={<LoadingScreen />} fallbackElement={<NotFoundRoute />}>
      {projectId && aiAgentEnabled && !aiAgentLoading ? (
        <ChatDrawerProvider>
          {content}
          <AiRuntimeProvider>
            <AiChatFab />
            <AiChatDrawer />
          </AiRuntimeProvider>
        </ChatDrawerProvider>
      ) : (
        content
      )}
    </AppAssignmentCheck>
  );
};
